import {
    ArrowLeft, Armchair, Building2, Droplet, Handshake, Home, Mail, Linkedin, Phone, RefreshCw,
    ShieldAlert, Sparkles, UtensilsCrossed, Wrench,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createProspectionList } from "../../interfaces/prospectionLists";
import { getSegmentStat, getSegmentTotalTracked, SegmentStat, SegmentId } from "../../interfaces/signals";

// ─── Use case definitions ──────────────────────────────────────────────────────

interface Segment {
    id: SegmentId;
    label: string;
    description: string;
    icon: React.ElementType;
    // Signals-based segments filter by signal type + a fixed lookback window (periodMonths,
    // default 12 — there is no user-facing period picker any more). Segments without a
    // signalType (e.g. equipment_renewal_5_7) rely purely on extraFilters and ignore the
    // window entirely, matching the /segment-stats backend logic.
    signalType?: string;
    periodMonths?: number;
    extraFilters?: Record<string, string>;
    recommended?: boolean;
}

const SEGMENTS: Segment[] = [
    { id: "furniture_renovation",       label: "Rénover le mobilier",              description: "Établissements de 7 à 10 ans, fenêtre de renouvellement du mobilier", icon: Armchair,        extraFilters: { businessAgeYears: "7-10" } },
    { id: "kitchen_equipment_install",  label: "Installer un équipement de cuisine", description: "Ouvertures récentes encore en cours d'équipement de cuisine", icon: UtensilsCrossed, extraFilters: { businessAgeYears: "0-2" } },
    { id: "used_oil_collection",        label: "Collecte des huiles usagées",      description: "Restauration traditionnelle et rapide, besoin récurrent de collecte", icon: Droplet,         extraFilters: { nafCode: "56.10A,56.10C" } },
    { id: "first_supplier_contact",     label: "Premier contact fournisseur",      description: "Ouvertures très récentes, pas encore approchées",           icon: Handshake,       signalType: "restaurant_opening", periodMonths: 2 },
    { id: "equipment_renewal_5_7",      label: "Anniversaire d'ouverture 5-7 ans", description: "Fenêtre de renouvellement équipement",                     icon: Wrench,          extraFilters: { businessAgeYears: "5-7" } },
    { id: "restaurant_opening",         label: "Nouvelles ouvertures",             description: "Restaurants récemment ouverts",                             icon: Home,            signalType: "restaurant_opening" },
    { id: "opening_with_email",         label: "Ouvertures avec email",            description: "Nouvelles ouvertures avec un email de contact disponible", icon: Mail,            signalType: "restaurant_opening", extraFilters: { contactFilters: "email" } },
    { id: "opening_with_linkedin",      label: "Ouvertures avec LinkedIn",         description: "Nouvelles ouvertures avec profil LinkedIn",                 icon: Linkedin,        signalType: "restaurant_opening", extraFilters: { contactFilters: "linkedin" } },
    { id: "owner_change",               label: "Changements d'exploitant",         description: "Restaurants ayant récemment changé de propriétaire",       icon: RefreshCw,       signalType: "restaurant_owner_change" },
    { id: "multi_site_5plus",           label: "Multi-site 5+",                    description: "Exploitants avec 5 établissements ou plus",                 icon: Building2,       signalType: "multi_site", extraFilters: { nbEtablissements: "5+" } },
    { id: "multi_site_2_4",             label: "Multi-site 2–4",                   description: "Exploitants avec 2 à 4 établissements",                    icon: Building2,       signalType: "multi_site", extraFilters: { nbEtablissements: "2,3,4" } },
    { id: "hygiene_control",            label: "Contrôles sanitaires",             description: "Établissements ayant subi un contrôle sanitaire",           icon: ShieldAlert,     signalType: "hygiene_control" },
];

// ─── Freshness / trend text ─────────────────────────────────────────────────────

function formatFreshness(iso?: string | null): string | null {
    if (!iso) return null;
    const diffH = (Date.now() - new Date(iso).getTime()) / 3_600_000;
    if (diffH < 1) return "màj à l'instant";
    if (diffH < 24) return `màj il y a ${Math.round(diffH)}h`;
    return `màj il y a ${Math.round(diffH / 24)}j`;
}

function trendText(stat: SegmentStat | null): string | null {
    if (!stat) return null;
    if (stat.trend && stat.trend.value > 0) return `+${stat.trend.value} ${stat.trend.label}`;
    return formatFreshness(stat.lastUpdatedAt);
}

// ─── Fixed lookback window (no user-facing period picker) ──────────────────────

function periodDates(months: number): { from: string; to: string } {
    const to = new Date(); to.setHours(23, 59, 59, 999);
    const from = new Date(); from.setMonth(from.getMonth() - months); from.setHours(0, 0, 0, 0);
    return { from: from.toISOString(), to: to.toISOString() };
}

/** Signal-bound segments get a fixed {from, to}; segments without a signalType get none. */
function segmentWindow(seg: Segment): { from?: string; to?: string } {
    if (!seg.signalType) return {};
    return periodDates(seg.periodMonths ?? 12);
}

// ─── Segment card ──────────────────────────────────────────────────────────────

function SegmentCard({
    seg,
    stat,
    creating,
    disabled,
    onCreate,
}: {
    seg: Segment;
    stat: SegmentStat | null;
    creating: boolean;
    disabled: boolean;
    onCreate: () => void;
}) {
    const Icon = seg.icon;
    const trend = trendText(stat);
    const isGrowthTrend = !!stat?.trend && stat.trend.value > 0;

    return (
        <button
            onClick={onCreate}
            disabled={disabled}
            className={`w-full flex flex-col gap-3 rounded-2xl border p-4 text-left transition-colors duration-150
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2
                disabled:opacity-60 disabled:cursor-not-allowed ${
                creating
                    ? "border-transparent bg-ink"
                    : "border-border bg-surface hover:border-border-strong"
            }`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className={`p-1.5 rounded-lg ${creating ? "bg-white/10" : "bg-surface-sunken"}`}>
                    {creating ? (
                        <span className="block w-4 h-4 border-2 border-white/30 border-t-white rounded-full motion-safe:animate-spin" />
                    ) : (
                        <Icon className="w-4 h-4 text-ink-muted" />
                    )}
                </div>
                <div className="text-right">
                    <span className={`font-sans text-lg font-semibold leading-tight block ${creating ? "text-ink-inverse" : "text-ink"}`}>
                        {stat ? stat.count.toLocaleString("fr-FR") : <span className={creating ? "text-ink-inverse-muted" : "text-ink-faint"}>—</span>}
                    </span>
                    {trend && (
                        <span className={`font-sans text-[10px] font-medium ${
                            creating ? "text-ink-inverse-muted" : isGrowthTrend ? "text-success" : "text-ink-faint"
                        }`}>
                            {trend}
                        </span>
                    )}
                </div>
            </div>

            <div>
                <p className={`font-sans text-sm font-semibold ${creating ? "text-ink-inverse" : "text-ink"}`}>{seg.label}</p>
                <p className={`font-sans text-xs mt-0.5 leading-relaxed ${creating ? "text-ink-inverse-muted" : "text-ink-muted"}`}>{seg.description}</p>
            </div>

            <div className={`flex items-center gap-3 pt-2 mt-auto border-t font-sans text-xs ${creating ? "border-white/10" : "border-border"}`}>
                <span className={`flex items-center gap-1 ${creating ? "text-ink-inverse-muted" : "text-ink-faint"}`}>
                    <Mail className="w-3 h-3" /> {stat?.channels.email ?? 0}
                </span>
                <span className={`flex items-center gap-1 ${creating ? "text-ink-inverse-muted" : "text-ink-faint"}`}>
                    <Linkedin className="w-3 h-3" /> {stat?.channels.linkedin ?? 0}
                </span>
                <span className={`flex items-center gap-1 ${creating ? "text-ink-inverse-muted" : "text-ink-faint"}`}>
                    <Phone className="w-3 h-3" /> {stat?.channels.phone ?? 0}
                </span>
            </div>
        </button>
    );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function DiscoverLists() {
    const navigate = useNavigate();

    const [segStats, setSegStats] = useState<Partial<Record<SegmentId, SegmentStat>>>({});
    const [totalTracked, setTotalTracked] = useState<number | null>(null);
    const [creatingId, setCreatingId] = useState<SegmentId | null>(null);
    const [createError, setCreateError] = useState<string | null>(null);

    // One request per card instead of a single bulk call: the grid fills in progressively
    // and a slow segment never blocks the others.
    useEffect(() => {
        let cancelled = false;

        SEGMENTS.forEach(seg => {
            const { from, to } = segmentWindow(seg);
            getSegmentStat(seg.id, from, to)
                .then(stat => { if (!cancelled) setSegStats(prev => ({ ...prev, [seg.id]: stat })); })
                .catch(err => console.error(`Segment ${seg.id} stats failed:`, err));
        });

        getSegmentTotalTracked()
            .then(d => { if (!cancelled) setTotalTracked(d.totalTracked); })
            .catch(err => console.error("Total tracked failed:", err));

        return () => { cancelled = true; };
    }, []);

    const loadingCounts = SEGMENTS.some(seg => !(seg.id in segStats));

    const handleCreateList = async (seg: Segment) => {
        if (creatingId) return;
        setCreatingId(seg.id);
        setCreateError(null);
        try {
            const { from, to } = segmentWindow(seg);
            await createProspectionList({
                name: seg.label,
                description: seg.description,
                type: "dynamic",
                filters: {
                    ...(seg.signalType ? { signalType: seg.signalType, signalFrom: from, signalTo: to } : {}),
                    ...seg.extraFilters,
                },
            });
            navigate("/prospection");
        } catch (e: any) {
            setCreateError(e?.response?.data?.message || "Erreur lors de la création");
            setCreatingId(null);
        }
    };

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-6 space-y-6">

                {/* Back */}
                <button
                    onClick={() => navigate("/prospection")}
                    className="flex items-center gap-1.5 text-ink-muted hover:text-ink text-xs transition-colors duration-150"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Retour aux listes
                </button>

                {/* ── Header ── */}
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-ink flex items-center justify-center shrink-0">
                        <Sparkles className="w-5 h-5 text-ink-inverse" />
                    </div>
                    <div className="flex-1">
                        <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">Découvrir des listes</h1>
                        <p className="text-sm text-ink-muted">Des segments prêts à l'emploi, à transformer en liste en un clic</p>
                    </div>
                </div>

                {/* ── Recommendations ── */}
                <div>
                    {/* Section header */}
                    {loadingCounts && (
                        <div className="mb-1">
                            <span className="text-xs text-ink-faint">Chargement…</span>
                        </div>
                    )}

                    <p className="text-xs text-ink-muted mb-4">
                        {totalTracked === null ? " " : `${totalTracked.toLocaleString("fr-FR")} établissements suivis`}
                    </p>

                    {createError && <p className="font-sans text-xs text-danger mb-4">{createError}</p>}

                    {/* Segment grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        {SEGMENTS.map(seg => (
                            <SegmentCard
                                key={seg.id}
                                seg={seg}
                                stat={segStats[seg.id] ?? null}
                                creating={creatingId === seg.id}
                                disabled={creatingId !== null}
                                onCreate={() => handleCreateList(seg)}
                            />
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
}
